import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import {
  useUploadMentorImage,
  type MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  VIBE_MIN_PHOTOS,
  VIBE_PROMPT_BANK,
  VIBE_SLOT_COUNT,
} from "@/lib/mentor-profile/constants";
import { uploadVerticalMentorImage } from "@/lib/mentor-profile/upload-vertical-image";
import { cn } from "@/lib/utils";
import { Scribbles } from "@somethings-ui/core/assets";
import { IconButton } from "@somethings-ui/web";
import { Loader2, Plus, RefreshCw, Sparkles, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { WizardStepLayout } from "./wizard-step-layout";
import type { WizardStepProps } from "./types";

interface VibeSlot {
  prompt: string;
  imageUrl?: string;
}

function shuffle<T>(items: readonly T[]): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function pickPrompts(count: number, used: Set<string> = new Set()): string[] {
  const available = VIBE_PROMPT_BANK.filter((prompt) => !used.has(prompt));
  const pool = available.length >= count ? available : [...VIBE_PROMPT_BANK];
  return shuffle(pool).slice(0, count);
}

function initSlots(draftImages: MentorProfileDraft["vibe_images"]): VibeSlot[] {
  const existing = (draftImages ?? []).slice(0, VIBE_SLOT_COUNT);
  const used = new Set(
    existing.map((image) => image.prompt).filter((prompt): prompt is string => !!prompt),
  );
  const fresh = pickPrompts(VIBE_SLOT_COUNT - existing.length, used);

  return Array.from({ length: VIBE_SLOT_COUNT }, (_, index) => {
    const saved = existing[index];
    if (saved?.imageUrl) {
      return {
        prompt: saved.prompt || fresh.shift() || VIBE_PROMPT_BANK[index],
        imageUrl: saved.imageUrl,
      };
    }
    return {
      prompt: saved?.prompt || fresh.shift() || VIBE_PROMPT_BANK[index],
    };
  });
}

function toDraftImages(slots: VibeSlot[]): MentorProfileDraft["vibe_images"] {
  return slots
    .filter((slot): slot is VibeSlot & { imageUrl: string } => !!slot.imageUrl)
    .map((slot) => ({ imageUrl: slot.imageUrl, prompt: slot.prompt }));
}

function VibeSlotCard({
  slot,
  uploading,
  onUpload,
  onRefresh,
  onClear,
}: {
  slot: VibeSlot;
  uploading: boolean;
  onUpload: (file: File) => void;
  onRefresh: () => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { url: signedUrl, isPending: isSigning } = useSignedMedia(
    slot.imageUrl,
  );
  const previewUrl = signedUrl ?? undefined;

  return (
    <div className="relative w-[132px] shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = "";
        }}
      />

      {slot.imageUrl ? (
        <div className="relative h-44 overflow-hidden rounded-2xl border-2 border-white shadow-[0_4px_14px_rgba(31,30,29,0.12)]">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              className="size-full object-cover"
            />
          ) : (
            <div className="flex size-full items-center justify-center bg-surfaces-z3">
              <Loader2
                className={cn(
                  "size-5 text-text-tertiary",
                  isSigning && "animate-spin",
                )}
              />
            </div>
          )}
          <IconButton
            size="sm"
            variant="glass"
            className="absolute right-1.5 top-1.5 bg-black/55 hover:bg-black/70"
            aria-label="Remove photo"
            icon={<X className="size-3.5" />}
            onClick={onClear}
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent px-2.5 pb-2.5 pt-8">
            <p className="font-body text-[11px] leading-tight text-white/90">
              {slot.prompt}
            </p>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={cn(
            "flex h-44 w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-text-primary/25 bg-text-primary/[0.02] px-3 pb-3.5 pt-12 text-center transition-colors hover:border-text-primary/40 hover:bg-text-primary/[0.04]",
            uploading && "opacity-60",
          )}
        >
          <span className="flex size-8 items-center justify-center rounded-full bg-text-primary/[0.06] text-text-primary">
            {uploading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
          </span>
          <span className="font-body text-xs font-medium leading-snug text-text-primary/75">
            {slot.prompt}
          </span>
        </button>
      )}

      {!slot.imageUrl && (
        <IconButton
          size="md"
          variant="ghost-black"
          className="absolute right-1 top-1"
          aria-label="Swap this prompt for another"
          icon={<RefreshCw className="size-4" />}
          onClick={onRefresh}
        />
      )}
    </div>
  );
}

export function VibeStep({
  draft,
  saveState,
  setField,
  embedded,
  reviewerNotes,
  onContinue,
  onBack,
}: WizardStepProps) {
  const upload = useUploadMentorImage();
  const [slots, setSlots] = useState<VibeSlot[]>(() =>
    initSlots(draft.vibe_images),
  );
  const slotsRef = useRef(slots);
  slotsRef.current = slots;
  const [uploadingIndexes, setUploadingIndexes] = useState<Set<number>>(
    () => new Set(),
  );
  const [error, setError] = useState<string | null>(null);

  const filledCount = useMemo(
    () => slots.filter((slot) => !!slot.imageUrl).length,
    [slots],
  );

  const refreshPrompt = (index: number) => {
    setSlots((prev) => {
      const used = new Set(prev.map((slot) => slot.prompt));
      const [nextPrompt] = pickPrompts(1, used);
      return prev.map((slot, i) =>
        i === index
          ? { ...slot, prompt: nextPrompt || VIBE_PROMPT_BANK[0] }
          : slot,
      );
    });
  };

  const clearSlot = (index: number) => {
    setError(null);
    const next = slotsRef.current.map((slot, i) =>
      i === index ? { prompt: slot.prompt, imageUrl: undefined } : slot,
    );
    slotsRef.current = next;
    setSlots(next);
    setField("vibe_images", toDraftImages(next));
  };

  const handleUpload = async (index: number, file: File) => {
    setError(null);
    setUploadingIndexes((prev) => new Set(prev).add(index));

    const result = await uploadVerticalMentorImage(file, {
      filename: file.name || `vibe-${index + 1}.jpg`,
      upload: (params) => upload.mutateAsync(params),
    });

    setUploadingIndexes((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });

    if (!result.ok) {
      setError(result.error);
      return;
    }

    const next = slotsRef.current.map((slot, i) =>
      i === index ? { ...slot, imageUrl: result.url } : slot,
    );
    slotsRef.current = next;
    setSlots(next);
    setField("vibe_images", toDraftImages(next));
  };

  const previewDraft = useMemo(
    () => ({ ...draft, vibe_images: toDraftImages(slots) }),
    [draft, slots],
  );

  return (
    <WizardStepLayout
      reviewerNotes={reviewerNotes}
      embedded={embedded}
      stepIndex={7}
      icon={
        <img
          src={Scribbles.orangeLoops}
          alt=""
          className="size-10 object-contain"
        />
      }
      heading="What's your vibe?"
      subheading="Visuals are a great way to show teens who you are. Help them get to know your vibe with photos, memes, and other snapshots of your life."
      saveState={saveState}
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={
        filledCount < VIBE_MIN_PHOTOS || uploadingIndexes.size > 0
      }
      preview={
        <MatchingProfilePreviewCard
          draft={previewDraft}
          focus={{ region: "vibe", zoom: 1.05 }}
        />
      }
    >
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {slots.map((slot, index) => (
          <VibeSlotCard
            key={index}
            slot={slot}
            uploading={uploadingIndexes.has(index)}
            onUpload={(file) => void handleUpload(index, file)}
            onRefresh={() => refreshPrompt(index)}
            onClear={() => clearSlot(index)}
          />
        ))}
      </div>

      {error ? (
        <span className="font-body text-sm text-states-error">{error}</span>
      ) : (
        <span className="font-body text-sm text-text-secondary">
          {filledCount}/{VIBE_MIN_PHOTOS} required · slots 4–5 optional
        </span>
      )}

      <div className="mt-1 flex items-start gap-2 rounded-lg bg-brand-ecru/60 px-3.5 py-3">
        <Sparkles className="mt-0.5 size-3.5 shrink-0 text-brand-tangelo" />
        <p className="font-body text-body-3 leading-snug text-text-primary/80">
          Mentors who add a few of these tend to connect more easily.
        </p>
      </div>
    </WizardStepLayout>
  );
}
